import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Download, AlertTriangle, ArrowUpDown, FileText, Trash2, UserPlus } from "lucide-react";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { formatCLP } from "@/lib/format";
import { toast } from "sonner";
import PostReportDialog from "@/components/PostReportDialog";

const STATUS_OPTS = [
  { v: "all", l: "Todos los estados" },
  { v: "submitted", l: "Enviada" },
  { v: "pending_access_validation", l: "En validación de ingreso" },
  { v: "in_review", l: "En revisión" },
  { v: "additional_info_required", l: "Info adicional" },
  { v: "reconsideration_requested", l: "Reevaluación" },
  { v: "processed", l: "Procesada" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, startImpersonation } = useAuth();
  const canDelete = ["admin", "superadmin"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const [iajad, setIajad] = useState("all");
  const [alerts, setAlerts] = useState("all");
  const [sort, setSort] = useState({ key: "submitted_at", dir: "asc" });

  const [showCreate, setShowCreate] = useState(false);
  const [showPost, setShowPost] = useState(false);

  const [cbRut, setCbRut] = useState("");
  const [cbName, setCbName] = useState("");
  const [cbEmail, setCbEmail] = useState("");
  const [cbLoading, setCbLoading] = useState(false);

  const createOnBehalf = async () => {
    if (!cbRut.trim()) { toast.error("Ingresa el RUT del socio."); return; }
    setCbLoading(true);
    try {
      const { data } = await api.post("/admin/applications/create-on-behalf", {
        rut: cbRut.trim(), name: cbName.trim() || null, email: cbEmail.trim() || null,
      });
      startImpersonation(data.token, data.user);
      setShowCreate(false);
      setCbRut(""); setCbName(""); setCbEmail("");
      toast.success(`Creando solicitud para ${data.user.name || data.user.rut}`);
      navigate("/panel");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCbLoading(false);
    }
  };

  const load = async () => {
    setRows(null);
    const params = {};
    if (status !== "all") params.status = status;
    if (iajad !== "all") params.iajad = iajad;
    if (alerts === "alerts") params.alerts = "true";
    if (alerts === "manual") params.manual = "true";
    if (alerts === "notinbase") params.notinbase = "true";
    try {
      const { data } = await api.get("/admin/applications", { params });
      setRows(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setRows([]); }
  };
  useEffect(() => { load(); }, [status, iajad, alerts]);

  const del = async (id) => {
    try {
      await api.delete(`/admin/applications/${id}`);
      toast.success("Solicitud eliminada.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sorted = useMemo(() => {
    if (!rows) return null;
    const r = [...rows];
    r.sort((a, b) => {
      const rank = (s) => (s === "processed" ? 2 : s === "additional_info_required" ? 1 : 0);
      const pa = rank(a.status), pb = rank(b.status);
      if (pa !== pb) return pa - pb;
      const va = a[sort.key], vb = b[sort.key];
      if (va == null) return 1; if (vb == null) return -1;
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [rows, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("eim_token");
      const resp = await fetch(`${API_BASE}/admin/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "becas_eim.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo exportar."); }
  };

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("eim_token");
      const params = status !== "all" ? `?status=${status}` : "";
      const resp = await fetch(`${API_BASE}/admin/report/pdf${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const d = new Date();
      const mes = d.toLocaleDateString("es-CL", { month: "long" });
      const fecha = `${d.getDate()} ${mes.charAt(0).toUpperCase()}${mes.slice(1)} ${d.getFullYear()}`;
      const a = document.createElement("a"); a.href = url; a.download = `reporte_reunion_becas ${fecha}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el reporte."); }
  };

  const Th = ({ k, children }) => (
    <th className="px-2 py-2.5 text-left text-[11px] font-semibold uppercase text-slate-500 cursor-pointer leading-tight" onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-1">{children} <ArrowUpDown className="w-3 h-3 opacity-50" /></span>
    </th>
  );

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-bold">Dashboard de becas</h1>
        <div className="flex gap-3">
          <Button onClick={() => setShowCreate(true)} className="rounded-xl" data-testid="btn-create-on-behalf"><UserPlus className="w-4 h-4 mr-2" /> Crear solicitud</Button>
          <Button onClick={downloadReport} variant="outline" className="rounded-xl border-2" data-testid="btn-report-pdf"><FileText className="w-4 h-4 mr-2" /> Reporte pre reunión (PDF)</Button>
          <Button onClick={() => setShowPost(true)} variant="outline" className="rounded-xl border-2" data-testid="btn-post-report-pdf"><FileText className="w-4 h-4 mr-2" /> Reporte post reunión (PDF)</Button>
          <Button onClick={exportCsv} variant="outline" className="rounded-xl border-2" data-testid="btn-export"><Download className="w-4 h-4 mr-2" /> Exportar CSV</Button>
        </div>
      </div>

      <PostReportDialog open={showPost} onOpenChange={setShowPost} />
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-on-behalf-dialog">
          <DialogHeader>
            <DialogTitle>Crear solicitud en nombre de un socio</DialogTitle>
            <DialogDescription>
              Ingresa el RUT del socio. Si está en la base EIM, la familia se autocompletará en el asistente.
              Se creará su cuenta (sin enviarle correo) y podrás completar el asistente de 5 pasos por él.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="cb-rut" className="text-xs font-semibold uppercase text-slate-500">RUT del socio</Label>
              <Input id="cb-rut" className="h-11 rounded-xl mt-1.5" placeholder="12.345.678-5"
                value={cbRut} onChange={(e) => setCbRut(e.target.value)} data-testid="cb-rut-input" />
            </div>
            <div>
              <Label htmlFor="cb-name" className="text-xs font-semibold uppercase text-slate-500">Nombre (opcional si está en la base)</Label>
              <Input id="cb-name" className="h-11 rounded-xl mt-1.5" placeholder="Nombre y apellidos"
                value={cbName} onChange={(e) => setCbName(e.target.value)} data-testid="cb-name-input" />
            </div>
            <div>
              <Label htmlFor="cb-email" className="text-xs font-semibold uppercase text-slate-500">Correo (opcional)</Label>
              <Input id="cb-email" type="email" className="h-11 rounded-xl mt-1.5" placeholder="correo@ejemplo.cl"
                value={cbEmail} onChange={(e) => setCbEmail(e.target.value)} data-testid="cb-email-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setShowCreate(false)} data-testid="cb-cancel-btn">Cancelar</Button>
            <Button className="rounded-xl" onClick={createOnBehalf} disabled={cbLoading} data-testid="cb-submit-btn">
              {cbLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continuar al asistente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-48 rounded-xl" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent></Select>
        <Select value={iajad} onValueChange={setIajad}><SelectTrigger className="w-40 rounded-xl" data-testid="filter-iajad"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">IAJAD: Todos</SelectItem><SelectItem value="true">IAJAD: Sí</SelectItem><SelectItem value="false">IAJAD: No</SelectItem></SelectContent></Select>
        <Select value={alerts} onValueChange={setAlerts}><SelectTrigger className="w-56 rounded-xl" data-testid="filter-alerts"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todas</SelectItem><SelectItem value="alerts">Con alertas</SelectItem><SelectItem value="manual">Revisión manual</SelectItem><SelectItem value="notinbase">Fuera de base EIM</SelectItem></SelectContent></Select>
      </div>

      {sorted === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : sorted.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-10 text-center text-muted-foreground">No hay solicitudes que coincidan.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-border overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-secondary/60 border-b border-border">
              <tr>
                <Th k="human_id">ID</Th><Th k="nombre">Nombre</Th><Th k="rut">RUT</Th>
                <Th k="iajad">IAJAD</Th><Th k="household_size">Tam.</Th>
                <Th k="family_gross_income">Ing. bruto</Th><Th k="adjusted_income">Ing. ajustado</Th>
                <Th k="requested_pct">% Sol.</Th><Th k="table_pct">% Tabla</Th><Th k="ih_scholarship_pct">% IH</Th><Th k="recommended_pct">% Rec.</Th>
                <Th k="status">Estado</Th><th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-500">Alertas</th>
                {canDelete && <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-500">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className={`border-b border-border cursor-pointer ${r.status === "processed" ? "bg-emerald-50 text-slate-500 hover:bg-emerald-100/70" : r.status === "additional_info_required" ? "bg-amber-50 hover:bg-amber-100/70" : "hover:bg-secondary/40"}`} onClick={() => navigate(`/admin/solicitud/${r.id}`)} data-testid={`admin-row-${r.id}`}>
                  <td className={`px-2 py-2.5 font-semibold whitespace-nowrap ${r.status === "processed" ? "text-emerald-800" : "text-primary"}`}>{r.human_id}</td>
                  <td className="px-2 py-2.5 min-w-[140px]">
                    {r.nombre}
                    {r.not_in_base && <span className="ml-2 inline-block rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold px-2 py-0.5 align-middle" data-testid={`tag-notinbase-${r.id}`}>Fuera de base</span>}
                  </td>
                  <td className="px-2 py-2.5 whitespace-nowrap text-muted-foreground">{r.rut}</td>
                  <td className="px-2 py-2.5">{r.iajad ? <span className="text-emerald-700 font-semibold">Sí</span> : "No"}</td>
                  <td className="px-2 py-2.5">{r.household_size ?? "—"}</td>
                  <td className="px-2 py-2.5 whitespace-nowrap">{formatCLP(r.family_gross_income)}</td>
                  <td className="px-2 py-2.5 whitespace-nowrap">{formatCLP(r.adjusted_income)}</td>
                  <td className="px-2 py-2.5">{r.accept_committee_pct ? "EIM" : (r.requested_pct != null ? `${r.requested_pct}%` : "—")}</td>
                  <td className="px-2 py-2.5">{r.table_pct != null ? `${r.table_pct}%` : "—"}</td>
                  <td className="px-2 py-2.5">{r.ih_scholarship_pct ? `${r.ih_scholarship_pct}%` : "—"}</td>
                  <td className={`px-2 py-2.5 font-bold ${r.status === "processed" ? "text-slate-500" : "text-primary"}`}>{r.recommended_pct != null ? `${r.recommended_pct}%` : "—"}</td>
                  <td className="px-2 py-2.5"><div className="flex items-center gap-2">
                    <StatusBadge status={r.public_status} />
                    {r.status === "pending_access_validation" && r.days_pending != null && (
                      <span className={`inline-flex items-center rounded-full text-[10px] font-semibold px-2 py-0.5 whitespace-nowrap ${r.days_pending >= 3 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`} data-testid={`tag-waiting-${r.id}`}>
                        esperando {r.days_pending}d
                      </span>
                    )}
                  </div></td>
                  <td className="px-2 py-2.5">{(r.alerts?.length > 0 || r.manual_review || r.not_in_base) && <AlertTriangle className="w-4 h-4 text-amber-600" />}</td>
                  {canDelete && (
                    <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="text-destructive hover:text-red-700" title="Eliminar solicitud" data-testid={`btn-delete-${r.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar esta solicitud?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Se eliminará permanentemente la solicitud <strong>{r.human_id}</strong> de <strong>{r.nombre}</strong> y todos sus datos asociados (documentos, cálculos y decisiones). Esta acción no se puede deshacer.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="btn-delete-cancel">Cancelar</AlertDialogCancel>
                            <AlertDialogAction className="bg-destructive hover:bg-red-700 text-white" onClick={() => del(r.id)} data-testid={`btn-delete-confirm-${r.id}`}>Eliminar</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
